import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, KeyRound, Plus, Shield, UserCog, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { API } from '@/lib/config';

const defaultForm = {
  name: '',
  email: '',
  username: '',
  password: '',
  is_admin: false,
  is_active: true
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingUser(null);
    setFormData(defaultForm);
    setShowForm(true);
  };

  const openEditForm = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      username: user.username || '',
      password: '',
      is_admin: Boolean(user.is_admin),
      is_active: Boolean(user.is_active)
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setEditingUser(null);
    setFormData(defaultForm);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        const payload = {
          name: formData.name,
          email: formData.email,
          username: formData.username,
          is_admin: formData.is_admin,
          is_active: formData.is_active
        };

        if (formData.password.trim()) {
          payload.password = formData.password;
        }

        await axios.put(`${API}/users/${editingUser.id}`, payload);
        toast.success('Usuario atualizado com sucesso');
      } else {
        await axios.post(`${API}/users`, {
          name: formData.name,
          email: formData.email,
          username: formData.username,
          password: formData.password,
          is_admin: formData.is_admin,
          is_active: formData.is_active
        });
        toast.success('Usuario criado com sucesso');
      }

      closeForm();
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuario');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <UserCog className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-manrope font-bold tracking-tight">Administracao de Usuarios</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 space-y-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-3xl font-manrope font-bold tracking-tight mb-2">
              Gerenciar acessos
            </h2>
            <p className="text-muted-foreground font-inter">
              {users.length} {users.length === 1 ? 'usuario cadastrado' : 'usuarios cadastrados'}
            </p>
          </div>
          {!showForm && (
            <Button
              data-testid="add-user-button"
              onClick={openCreateForm}
              className="rounded-full h-12 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Novo Usuario
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-border/40 p-5 shadow-sm">
            <p className="text-sm text-muted-foreground mb-1">Administradores ativos</p>
            <p className="text-3xl font-manrope font-bold">
              {users.filter((user) => user.is_admin && user.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-border/40 p-5 shadow-sm">
            <p className="text-sm text-muted-foreground mb-1">Usuarios ativos</p>
            <p className="text-3xl font-manrope font-bold">
              {users.filter((user) => user.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-border/40 p-5 shadow-sm">
            <p className="text-sm text-muted-foreground mb-1">Usuarios inativos</p>
            <p className="text-3xl font-manrope font-bold">
              {users.filter((user) => !user.is_active).length}
            </p>
          </div>
        </div>

        {showForm && (
          <div className="bg-white rounded-2xl border-2 border-border/50 p-8 shadow-lg">
            <h3 className="text-2xl font-manrope font-bold tracking-tight mb-6">
              {editingUser ? 'Editar Usuario' : 'Novo Usuario'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Usuario</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    {editingUser ? 'Nova senha' : 'Senha'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'Preencha apenas se quiser trocar' : 'Minimo de 6 caracteres'}
                    required={!editingUser}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center justify-between rounded-2xl border border-border/40 p-4">
                  <div>
                    <p className="font-medium">Perfil administrador</p>
                    <p className="text-sm text-muted-foreground">Permite acesso as telas administrativas</p>
                  </div>
                  <Switch
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                  />
                </div>

                <div className="flex items-center justify-between rounded-2xl border border-border/40 p-4">
                  <div>
                    <p className="font-medium">Usuario ativo</p>
                    <p className="text-sm text-muted-foreground">Usuarios inativos nao conseguem autenticar</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-sm text-blue-900">
                O usuario padrao tambem pode ser editado nesta tela. O sistema bloqueia a remocao do ultimo administrador ativo.
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1 rounded-full h-12">
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="flex-1 rounded-full h-12">
                  {saving ? 'Salvando...' : editingUser ? 'Atualizar Usuario' : 'Criar Usuario'}
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {users.map((user) => (
            <div
              key={user.id}
              data-testid={`user-card-${user.id}`}
              className="bg-white rounded-2xl border border-border/40 p-6 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-2xl font-manrope font-bold tracking-tight">{user.name}</h3>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <p className="text-sm text-muted-foreground">Usuario: {user.username}</p>
                </div>
                <Button
                  data-testid={`edit-user-button-${user.id}`}
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  onClick={() => openEditForm(user)}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`text-xs px-3 py-1.5 rounded-full border ${
                  user.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-700 border-gray-200'
                }`}>
                  {user.is_active ? 'Ativo' : 'Inativo'}
                </span>
                {user.is_admin && (
                  <span className="text-xs px-3 py-1.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                    Administrador
                  </span>
                )}
                {user.is_default && (
                  <span className="text-xs px-3 py-1.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                    Usuario padrao
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl bg-gray-50 p-4">
                  <p className="text-muted-foreground mb-1">Seguranca</p>
                  <p className="font-medium flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-primary" />
                    Senha alteravel pelo admin
                  </p>
                </div>
                <div className="rounded-xl bg-gray-50 p-4">
                  <p className="text-muted-foreground mb-1">Perfil</p>
                  <p className="font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    {user.is_admin ? 'Acesso administrativo' : 'Acesso padrao'}
                  </p>
                </div>
              </div>

              {user.created_at && (
                <p className="text-xs text-muted-foreground mt-4">
                  Criado em {new Date(user.created_at).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          ))}
        </div>

        {users.length === 0 && (
          <div className="text-center py-16 px-4 bg-white rounded-2xl border border-border/40 shadow-sm">
            <Calendar className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-manrope font-semibold mb-2">Nenhum usuario cadastrado</h3>
            <p className="text-muted-foreground mb-6">Crie o primeiro acesso administrativo</p>
            <Button onClick={openCreateForm} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Criar Usuario
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
